import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'
import { signInWithEthereum } from '../lib/auth'

export interface ReportParams {
  targetAddress: string
  round: number
  reason: string
  reasonDetail: string
}

export function parseReportError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('user rejected') || msg.includes('ACTION_REJECTED'))
      return '트랜잭션을 거부했습니다'
    if (msg.includes('already reported')) return '이미 신고한 인증입니다'
    if (msg.includes('own proof') || msg.includes('self')) return '본인의 인증은 신고할 수 없습니다'
    return msg.slice(0, 120)
  }
  return '신고 제출에 실패했습니다'
}

type FileReportFn = (target: string, round: number) => Promise<ethers.TransactionResponse>

export function useReport(challengeAddress: string) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, ReportParams>({
    mutationFn: async ({ targetAddress, round, reason, reasonDetail }) => {
      if (!window.ethereum) throw new Error('MetaMask가 설치되어 있지 않습니다')

      // Step 1: On-chain fileReport → emit ReportFiled event to get reportId
      const browserProvider = new ethers.BrowserProvider(window.ethereum)
      const signer = await browserProvider.getSigner()
      const contract = new ethers.Contract(challengeAddress, CHALLENGE_ABI, signer)

      const tx = await (contract.fileReport as FileReportFn)(targetAddress, round)
      const receipt = await tx.wait()

      let reportId: string | null = null
      if (receipt) {
        for (const log of receipt.logs) {
          try {
            const parsed = contract.interface.parseLog({
              topics: log.topics as string[],
              data: log.data,
            })
            if (parsed?.name === 'ReportFiled') {
              reportId = String(parsed.args.reportId as bigint)
              break
            }
          } catch {
            // ignore unparseable logs
          }
        }
      }

      // Step 2: Store report data in Workers API (non-fatal).
      // Requires a wallet-signed JWT; the server checks the JWT address == reporter.
      const reporterAddress = (await signer.getAddress()).toLowerCase()
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
      try {
        const jwt = await signInWithEthereum()
        await fetch(`${apiBase}/api/reports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            reportId,
            challengeAddress,
            reporter: reporterAddress,
            target: targetAddress.toLowerCase(),
            round,
            reason,
            reasonDetail,
          }),
        })
      } catch {
        // Non-fatal: off-chain save failed
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['proofs', challengeAddress] })
    },
  })
}
